import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  TemplateModel,
  WorkTemplateTaskModel,
} from "../factories/templates/models/template.model";
import { workSlice } from "./WorkSlice";
import { workTasksSlice } from "./WorkTasksSlice";

export const templatesSlice = createApi({
  reducerPath: "templatesSlice",
  tagTypes: ["Template"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getTemplates: build.query<
      TemplateModel[],
      {
        orgId: string;
        status?: "all" | "active" | "inactive";
      }
    >({
      query: ({ orgId, status = "active" }) => ({
        url: "api/work-template/get-all",
        params: { orgId, status },
        method: "GET",
      }),
      providesTags: ["Template"],
    }),
    getTemplate: build.query<
      TemplateModel,
      {
        id: string;
      }
    >({
      query: ({ id }) => ({
        url: "api/work-template/" + id,
        method: "GET",
      }),
      providesTags: ["Template"],
    }),
    searchTemplate: build.query<
      TemplateModel[],
      {
        orgId: string;
        query: string | null;
      }
    >({
      query: (params) => ({
        url: "api/work-template/search",
        method: "GET",
        params,
      }),
      providesTags: ["Template"],
    }),
    updateTemplate: build.mutation<unknown, TemplateModel>({
      query: (body) => ({
        url: "api/work-template/update-template",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Template"],
    }),
    addTemplate: build.mutation<
      { id: string },
      {
        templateName: string;
        description: string;
        workTemplateTasks: [];
        orgId: null;
        orgRelayId: string;
        isActive: boolean;
      }
    >({
      query: (body) => ({
        url: "api/work-template/add-template",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Template"],
    }),
    getDeactivatedTemplates: build.query<
      TemplateModel[],
      {
        orgId: string;
      }
    >({
      query: (params) => ({
        url: "api/work-template/hidden-templates",
        params,
        method: "GET",
      }),
      providesTags: ["Template"],
    }),
    deactivateTemplate: build.mutation<unknown, TemplateModel>({
      query: (body) => ({
        url: "api/work-template/hide-template",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Template"],
    }),
    activateTemplate: build.mutation<unknown, TemplateModel>({
      query: (body) => ({
        url: "api/work-template/unhide-template",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Template"],
    }),
    deleteTemplate: build.mutation<unknown, TemplateModel>({
      query: (body) => ({
        url: "api/work-template",
        body,
        method: "DELETE",
      }),
      invalidatesTags: ["Template"],
      // Work items lose the link to the template (QA batch 12): refresh open work pages.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } finally {
          dispatch(workSlice.util.invalidateTags(["Work"]));
          dispatch(workTasksSlice.util.invalidateTags(["WorkTask"]));
        }
      },
    }),
    /** QA batch 12: how many work items use this template (delete confirmation). */
    getTemplateUsage: build.query<{ workItems: number }, string>({
      query: (id) => ({
        url: `api/work-template/${id}/usage`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    addSubtask: build.mutation<
      unknown,
      {
        workTemplateId: string;
        name: string;
        description: string;
        timeBudget: number;
        assignedUserId?: string;
        noOfDays?: number | null;
        isAfterStartDate?: boolean;
      }
    >({
      query: (body) => ({
        url: "api/work-template/add-template-task",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Template"],
    }),
    updateSubtask: build.mutation<unknown, WorkTemplateTaskModel>({
      query: (body) => ({
        url: "api/template-task/update-template-task",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Template"],
    }),
    deleteSubtask: build.mutation<unknown, WorkTemplateTaskModel>({
      query: (body) => ({
        url: "api/template-task",
        body,
        method: "DELETE",
      }),
      invalidatesTags: ["Template"],
    }),
    reorderSubtask: build.mutation<unknown, string[]>({
      query: (body) => ({
        url: "api/template-task/change-order",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Template"],
    }),
    importTemplates: build.mutation<unknown, { orgId: string; body: FormData }>(
      {
        query: ({ orgId, body }) => ({
          url: "api/work-template/import-templates",
          params: { orgId },
          body,
          method: "POST",
        }),
        invalidatesTags: ["Template"],
      },
    ),
    duplicateTemplates: build.mutation<
      unknown,
      { orgId: string; templateId: string; templateName: string }
    >({
      query: (params) => ({
        url: "/api/work-template/duplicate-template",
        params,
        method: "POST",
      }),
      invalidatesTags: ["Template"],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useLazyGetTemplateQuery,
  useUpdateTemplateMutation,
  useAddTemplateMutation,
  useLazyGetTemplatesQuery,
  useLazyGetDeactivatedTemplatesQuery,
  useActivateTemplateMutation,
  useDeactivateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateUsageQuery,
  useAddSubtaskMutation,
  useUpdateSubtaskMutation,
  useDuplicateTemplatesMutation,
  useReorderSubtaskMutation,
  useDeleteSubtaskMutation,
  useImportTemplatesMutation,
  useLazySearchTemplateQuery,
} = templatesSlice;
